import { useUpdateColumnFromCellByValueAndOperation } from "./useUpdateColumnFromCellByValueAndOperation";
import { useUpdateCellByValueAndOperation } from "./useUpdateCellByValueAndOperation";
import { copyWithHeadersAndMonths } from "../copyRangeSelectionHelpers";

import { useOptionalActivePageId, usePage, usePageFormattingOptions, useUpdatePageFormatting } from "../../../data";
import type { TGridKeyboardShortcut } from "./utils";
import { getProcessedSelectedCellsByRange } from "../../../tableUtils";
import type { GridApi } from "ag-grid-community";
import { getCellIds } from "../cell-style/helpers";

export const useKeyboardShortcuts = () => {
  const pageId = useOptionalActivePageId();
  const page = usePage(pageId ?? "");
  const updateColumnFromCellByValueAndOperation = useUpdateColumnFromCellByValueAndOperation();
  const updateCellsByValueAndOperation = useUpdateCellByValueAndOperation();
  const toggleBold = useBoldToggle();

  return [
    {
      name: "Increment cell",
      description: "Increment the selected cell(s)",
      shortcuts: ["ctrl+ArrowUp", "cmd+shift+ArrowUp", "ctrl+i", "cmd+shift+i"],
      action: ({ params }) => {
        updateCellsByValueAndOperation({ api: params.api, operation: "+" });
      },
    },
    {
      name: "Decrement cell",
      description: "Decrement the selected cell(s)",
      shortcuts: ["ctrl+ArrowDown", "cmd+shift+ArrowDown", "ctrl+d", "cmd+shift+d"],
      action: ({ params }) => {
        updateCellsByValueAndOperation({ api: params.api, operation: "-" });
      },
    },
    {
      name: "Increment column (below)",
      description: "Increment the selected column(s)",
      shortcuts: ["ctrl+alt+ArrowUp", "cmd+alt+shift+ArrowUp", "ctrl+shift+i", "cmd+shift+i"],
      action: ({ params }) =>
        updateColumnFromCellByValueAndOperation({
          api: params.api,
          column: params.column,
          operation: "+",
        }),
    },
    {
      name: "Decrement column (below)",
      description: "Decrement the selected column(s)",
      shortcuts: ["ctrl+alt+ArrowDown", "cmd+alt+shift+ArrowDown", "ctrl+shift+d", "cmd+shift+d"],
      action: ({ params }) =>
        updateColumnFromCellByValueAndOperation({
          api: params.api,
          column: params.column,
          operation: "-",
        }),
    },
    {
      name: "Copy with headers and months",
      description: "Copy the selected range with headers and months",
      // only ctrl+shift+C here, cmd+shift+C conflicts with chrome devtools on mac
      shortcuts: ["ctrl+shift+C"],
      action: ({ params }) => copyWithHeadersAndMonths(params),
    },
    {
      name: "Make cell bold",
      description: "Format the selected cell to be bold",
      shortcuts: ["ctrl+B", "cmd+B"],
      action: ({ params }) => {
        if (!page?.data) return;
        toggleBold(params);
      },
    },
  ] satisfies TGridKeyboardShortcut[];
};

const useBoldToggle = () => {
  "use no memo"; // FIXME: REACT-COMPILER - Error: This value cannot be modified

  const pageId = useOptionalActivePageId();
  const updateFormatting = useUpdatePageFormatting();
  const pageFormatting = usePageFormattingOptions(pageId);

  return ({ api }: { api: GridApi }) => {
    if (!pageId) return;
    const ranges = getProcessedSelectedCellsByRange({ api });
    const { cellIds } = getCellIds({ api, ranges, type: "range" });
    const cell_highlights = pageFormatting.data?.cell_highlights ?? {};

    for (const cellId of cellIds) {
      const highlight = cell_highlights[cellId] || {};
      const bold = highlight.boldText || false;
      cell_highlights[cellId] = {
        ...cell_highlights[cellId],
        boldText: !bold,
      };
    }

    updateFormatting({ id: pageId, cell_highlights });
  };
};
